export default class DemoForm {
    constructor() {
        this.form = document.querySelector('.demo form') || document.querySelector('.demo__inner form');
        if (!this.form) {
            return;
        }

        this.submitButton = this.form.querySelector('button[type="submit"]');
        this.feedback = this.form.querySelector('.demo__feedback') || document.getElementById('demo-form-feedback');

        if (!this.feedback) {
            this.feedback = document.createElement('div');
            this.feedback.className = 'demo__feedback w-full';
            this.feedback.id = 'demo-form-feedback';
            this.feedback.setAttribute('role', 'status');
            this.feedback.setAttribute('aria-live', 'polite');
            if (this.submitButton) {
                this.form.insertBefore(this.feedback, this.submitButton);
            } else {
                this.form.appendChild(this.feedback);
            }
        }

        this.init();
    }

    init() {
        this.initWaitingState();
        this.form.addEventListener('input', (e) => this.handleInput(e));
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    getRequiredInputs() {
        return [...this.form.querySelectorAll('.field__input')].filter((input) => input.id !== 'med');
    }

    isEmpty(input) {
        return input.value.trim() === '';
    }

    setFieldError(input, hasError) {
        const field = input.closest('.field');
        if (!field) return;

        let errorMsg = field.querySelector('.field__error-msg');

        if (!hasError) {
            errorMsg?.remove();
            input.classList.remove('field__input--error');
            input.removeAttribute('aria-invalid');
            input.removeAttribute('aria-describedby');
            return;
        }

        if (!errorMsg) {
            errorMsg = document.createElement('p');
            errorMsg.className = 'field__error-msg';
            errorMsg.id = `${input.id}-error`;
            errorMsg.textContent = 'Ovo polje je obavezno.';
            field.appendChild(errorMsg);
        }

        input.classList.add('field__input--error');
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', errorMsg.id);
    }

    handleInput(event) {
        const input = event.target;
        if (!this.getRequiredInputs().includes(input)) return;

        this.setFieldError(input, this.isEmpty(input));
    }

    validate() {
        const invalidInputs = this.getRequiredInputs().filter((input) => this.isEmpty(input));

        this.getRequiredInputs().forEach((input) => this.setFieldError(input, invalidInputs.includes(input)));
        invalidInputs[0]?.focus();

        return invalidInputs.length === 0;
    }

    initWaitingState() {
        if (!this.submitButton) return;

        if (!this.submitButton.classList.contains('btn--waiting')) {
            this.submitButton.classList.add('btn--waiting');
        }
        this.submitButton.disabled = true;
        this.isWaiting = true;

        const completeWaiting = () => {
            if (!this.isWaiting) return;
            this.isWaiting = false;
            this.submitButton.classList.remove('btn--waiting');
            this.submitButton.disabled = false;
        };

        this.waitingTimeout = setTimeout(completeWaiting, 10000);

        this.submitButton.addEventListener('animationend', (e) => {
            if (e.animationName === 'btn-fill') {
                clearTimeout(this.waitingTimeout);
                completeWaiting();
            }
        }, { once: true });
    }

    checkHoneypot(form = this.form) {
        const targetForm = form && form.jquery ? form[0] : (form || this.form);
        const honeypotValue = targetForm ? (targetForm.querySelector('#med')?.value ?? '') : '';

        if (honeypotValue !== '') {
            window.location.replace('about:blank');
            setTimeout(() => {
                history.pushState(null, null, 'about:blank');
                history.pushState(null, null, 'about:blank');
                history.go(-2);
            }, 0);
            return false; // Spam detected
        }

        return true; // Legitimate submission
    }

    setFeedback(message, type = 'success') {
        if (!this.feedback) return;
        this.feedback.textContent = message;
        this.feedback.className = `demo__feedback w-full demo__feedback--${type}`;
    }

    clearFeedback() {
        if (!this.feedback) return;
        this.feedback.textContent = '';
        this.feedback.className = 'demo__feedback w-full';
    }

    setLoading(isLoading) {
        if (isLoading) {
            this.form.setAttribute('aria-busy', 'true');
            if (this.submitButton) {
                this.submitButton.disabled = true;
                this.submitButton.dataset.originalText = this.submitButton.textContent;
                this.submitButton.textContent = 'Šalje se...';
            }
        } else {
            this.form.removeAttribute('aria-busy');
            if (this.submitButton) {
                this.submitButton.disabled = false;
                if (this.submitButton.dataset.originalText) {
                    this.submitButton.textContent = this.submitButton.dataset.originalText;
                }
            }
        }
    }

    async handleSubmit(event) {
        event.preventDefault();

        if (this.isWaiting || this.submitButton?.classList.contains('btn--waiting')) {
            return;
        }

        if (!this.checkHoneypot(this.form)) {
            return;
        }

        if (!this.validate()) {
            return;
        }

        this.clearFeedback();
        this.setLoading(true);

        const formData = new FormData(this.form);
        formData.append('action', 'w4d_submit_demo_form');

        const nonce = (typeof w4d_frontend !== 'undefined' && w4d_frontend?.nonce)
            ? w4d_frontend.nonce
            : (window.w4d_frontend?.nonce || '');
        formData.append('_nonce', nonce);

        const ajaxUrl = (typeof w4d_frontend !== 'undefined' && w4d_frontend?.url)
            ? w4d_frontend.url
            : (window.w4d_frontend?.url || '/wp-admin/admin-ajax.php');

        try {
            const response = await fetch(ajaxUrl, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            const result = await response.json().catch(() => null);

            if (response.ok && result?.success) {
                const successMsg = result?.data?.message || 'Vaš zahtjev je uspješno poslan.';
                this.setFeedback(successMsg, 'success');
                this.form.reset();
            } else {
                const errorMsg = result?.data?.message || 'Došlo je do pogreške prilikom slanja zahtjeva. Molimo pokušajte ponovno.';
                this.setFeedback(errorMsg, 'error');
            }
        } catch (error) {
            this.setFeedback('Došlo je do mrežne pogreške. Molimo provjerite vezu i pokušajte ponovno.', 'error');
        } finally {
            this.setLoading(false);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new DemoForm());
} else {
    new DemoForm();
}
